/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Fix unused vars

import colorLib from '@kurkle/color';
import { type ChartData } from 'chart.js';
import { useMemo } from 'react';
import { AppTabularDto } from '../../../../../components/reports/dtos/app-tabular.dto';
import { WaterfallData } from './waterfall-data.interface';

const BAD_COLOR = '#FF9999';
const GOOD_COLOR = '#A1E3CB';
const NEUTRAL_COLOR = '#B1E3FF';

export interface WaterfallDetails {
  details?: any[];
}
export const useLineChartData2 = (rows?: WaterfallData[]) => {
  const chartData = useMemo(() => {
    let dd: WaterfallData[] = rows ?? [];
    if (dd.length === 1) {
      // Workaround to display only one week in the middle.
      const dummy = dd.map((i) => ({ ...i, label: '  ' }));
      const dummy2 = dd.map((i) => ({ ...i, label: ' ' }));
      dd = dummy.concat(dd).concat(dummy2);
    }
    const colors = dd.map((i) => i.color);
    const ret: ChartData<'line', WaterfallData[]> = {
      //labels,
      datasets: [
        {
          label: 'Impact',
          data: dd,
          pointRadius: 1,
          fill: {
            target: 'origin',
            above: GOOD_COLOR, // Area will be red above the origin
            below: BAD_COLOR, // And blue below the origin
          },
          backgroundColor: colors,
          segment: {
            borderWidth: 0,
          },
        },
      ],
    };
    return ret;
  }, [rows]);
  return chartData;
};
const addTotals = (
  variable: WaterfallData,
  row: Record<string, any>,
  value: number,
) => {
  let week = variable.totals?.find((b) => b.label === row.WEEK);
  if (!week) {
    variable.totals = variable.totals ?? [];
    week = createWaterfallData(row.WEEK, row.WEEK, value, getColor(value));
    variable.totals.push(week);
  } else {
    week.value += value;
    week.color = getColor(week.value);
  }
  return variable;
};
const getColor = (value: number) => {
  return value < 0 ? BAD_COLOR : GOOD_COLOR;
};
const createWaterfallData = (
  id: string,
  label: string,
  value: number,
  color: string,
  max: number = value,
): WaterfallData => {
  return { id, label, value, children: [], color, max };
};
const sortFunction = (a: WaterfallData, b: WaterfallData) =>
  a.id > b.id ? 1 : -1;
const useWaterfallChartGroupData = (
  data: AppTabularDto<Record<string, any>> | undefined,
) => {
  const chartData = useMemo(() => {
    const rows = data?.rows ?? [];
    const variablesDict: Record<string, WaterfallData> = {};

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const value = row.DECOMP_IMPACT;
      // Group by Variable name
      let variable: WaterfallData | undefined = undefined;
      if (!variablesDict[row.WATERFALL_NAME]) {
        variable = createWaterfallData(
          row.WATERFALL_NAME,
          row.WATERFALL_NAME_DESC ?? row.WATERFALL_NAME,
          value,
          getColor(value),
        ); // { label: row.WATERFALL_NAME, value, children: [], color:getColor(value) }
      } else {
        variable = variablesDict[row.WATERFALL_NAME];
        variable.value += value;

        variable.color = getColor(variable.value);
      }
      // Then group by Comp Brand
      let compBrand = variable.children?.find(
        (b) => b.label === row.COMP_IRIS_BRAND,
      );
      if (!compBrand) {
        variable.children = variable.children ?? [];
        compBrand = createWaterfallData(
          row.COMP_IRIS_BRAND,
          row.COMP_IRIS_BRAND,
          value,
          getColor(value),
        ); // { label: row.COMP_IRIS_BRAND, value, color:getColor(value) }
        variable.children.push(compBrand);
      } else {
        compBrand.value += value;
        compBrand.color = getColor(compBrand.value);
        //compBrand.rows?.push(row)
      }
      variable.max =
        compBrand.value > variable.max ? compBrand.value : variable.max;

      // Finally, period details
      compBrand.children = compBrand.children ?? [];
      compBrand.children.push(
        createWaterfallData(row.WEEK, row.WEEK, value, getColor(value)),
      );
      variablesDict[row.WATERFALL_NAME] = addTotals(variable, row, value);
    }

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const value = row.DECOMP_IMPACT;
      // Total
      let total: WaterfallData | undefined = undefined;
      if (!variablesDict['TOTAL']) {
        total = createWaterfallData('Total', 'Total', value, NEUTRAL_COLOR); // { label: 'Total', value, children: [], color:NEUTRAL_COLOR }
      } else {
        total = variablesDict['TOTAL'];
        total.value += value;
        //total.color = getColor(total.value)
      }
      variablesDict['TOTAL'] = addTotals(total, row, value);
    }
    const ret: WaterfallData[] = [];
    Object.entries(variablesDict).forEach(([key, value]) => {
      // Workaround: different WATERFALL_ORDER for same variable causes issues in the order of the dates.
      // Until back-end is fixed, this will ensure the correct order.
      value.totals = value.totals?.sort(sortFunction);
      value.children?.forEach((i) => {
        i.children = i.children?.sort(sortFunction);
      });
      ret.push(value);
    });
    return ret;
  }, [data]);
  return chartData;
};

export const useWaterfallChartData = (
  data: AppTabularDto<Record<string, any>> | undefined,
) => {
  const groupedData = useWaterfallChartGroupData(data);

  const chartData = useMemo(() => {
    let acc = 0;
    const colors: string[] = [];
    const dd: WaterfallData[] = groupedData.concat();

    for (let i = 0; i < dd.length; i++) {
      const row = dd[i];
      const value = row.value;
      colors.push(row.color);
      if (row.label === 'Total') {
        row.chartValue = [0, value];
      } else {
        row.chartValue = [acc, acc + value];
      }

      acc = acc + value;
    }

    //dd.push({ label: 'Total', value: acc, chartValue: [0, acc] })
    //colors.push(NEUTRAL_COLOR)

    const ret: ChartData<'bar', WaterfallData[]> = {
      //labels,
      datasets: [
        {
          label: 'Impact',
          data: dd,
          backgroundColor: colors,
          hoverBackgroundColor: colors.map((i) =>
            colorLib(i).darken(0.2).rgbString(),
          ),
          // hoverBorderColor:colors.map(i=>colorLib(i).darken(.2).rgbString()),
          // borderWidth:2
        },
      ],
    };
    return ret;
  }, [groupedData]);

  return chartData;
};
